import React, { ReactNode, useId } from "react";
import FormLabel, { FormLabelBadgePosition } from "./FormLabel";
import InputError, { InputErrorVariant } from "./InputError";

import {
  Select as ChakraSelect,
  SelectProps as ChakraSelectProps,
  FormLabelProps,
  forwardRef,
  Box,
  Stack,
} from "@chakra-ui/react";

export type SelectProps = {
  label?: string;
  labelProps?: FormLabelProps;
  required?: boolean;
  showNotRequired?: boolean;
  badgePosition?: FormLabelBadgePosition;
  error?: string;
  invalid?: boolean;
  id?: string;
  unpublished?: boolean;
  placeholder?: string;
  children?: ReactNode;
  errorVariant?: InputErrorVariant;
  message?: React.ReactNode;
} & ChakraSelectProps;

const Select = forwardRef<SelectProps, "select">(
  (
    {
      label = "",
      labelProps,
      required = false,
      showNotRequired = false,
      badgePosition = "after",
      error = "",
      invalid = undefined,
      id: propId = "",
      unpublished = false,
      placeholder = "",
      children,
      errorVariant = "text",
      message = "",
      ...props
    },
    ref,
  ) => {
    const randomId = useId();
    const id = propId !== "" ? propId : randomId;

    return (
      <Stack width="100%" gap={0}>
        {label !== "" && (
          <FormLabel
            {...labelProps}
            required={required}
            showNotRequired={showNotRequired}
            badgePosition={badgePosition}
            htmlFor={id}
            unpublished={unpublished}
          >
            {label}
          </FormLabel>
        )}
        <ChakraSelect
          placeholder={placeholder}
          mt={label !== "" ? 1 : undefined}
          borderRadius="2px"
          border="1px solid #99A9B0"
          errorBorderColor="#CD4429"
          fontSize={{ base: "sm", sm: "md" }}
          id={id}
          isInvalid={invalid === true ? true : error !== ""}
          {...props}
          ref={ref}
          bgColor="white"
        >
          {children}
        </ChakraSelect>
        {message !== "" && (
          <Box color="gray.700" fontSize="sm" mt={1}>
            {message}
          </Box>
        )}
        {error !== "" && (
          <Box mt={2}>
            <InputError variant={errorVariant}>{error}</InputError>
          </Box>
        )}
      </Stack>
    );
  },
);

export default Select;
